import { Component } from '@theme/component';
import { scrollIntoView } from '@theme/scrolling';

/**
 * Adds mouse/pen click-and-drag horizontal scrolling to a scrollable child.
 *
 * Touch devices already drag-scroll natively, so this only engages for mouse/pen
 * pointers, and only when the content actually overflows. Any click that would
 * fire on a link/button at the end of a drag is suppressed.
 *
 * Usage — put `ref="scroller"` on the element that scrolls (falls back to the
 * host element if omitted):
 *   <drag-scroll>
 *     <div ref="scroller" class="…overflow-x:auto…">…pills…</div>
 *   </drag-scroll>
 *
 * Add `scroll-active-into-view` to centre the `[aria-current]` child on load,
 * for a nav whose selected item can start off-screen.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [scroller]
 *
 * @extends {Component<Refs>}
 */
class DragScroll extends Component {
  /** @type {number | null} */
  #pointerId = null;
  #startX = 0;
  #startLeft = 0;
  #moved = false;
  /** @type {string} */
  #savedSnap = '';

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerdown', this.#handlePointerDown);
    // Capture phase so a drag is swallowed before a link/button click acts on it.
    this.addEventListener('click', this.#handleClick, { capture: true });
    // Links and images start a native drag-and-drop on mousedown, which would
    // hijack the scroll gesture — suppress it inside the scroller.
    this.addEventListener('dragstart', this.#preventDrag);

    if (this.hasAttribute('scroll-active-into-view')) this.#scrollActiveIntoView();
  }

  #scrollActiveIntoView = () => {
    const el = this.#scroller;
    const active = el.querySelector('[aria-current]');
    if (!active) return;

    // Scoped to the scroller so bringing the pill into view never moves the page.
    scrollIntoView(active, { ancestor: el, behavior: 'smooth', inline: 'center' });
  };

  /**
   * Scroll to the end of the row, for a host that has just added something there.
   *
   * Here rather than on the host: what scrolls is this component's business, and a host
   * reaching for it would have to know how the markup is put together.
   *
   * @param {object} [options]
   * @param {ScrollBehavior} [options.behavior] - Defaults to smooth, or instant when the
   *   shopper asked for less motion.
   */
  scrollToEnd({ behavior } = {}) {
    const el = this.#scroller;
    if (el.scrollWidth <= el.clientWidth) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    el.scrollTo({ left: el.scrollWidth, behavior: behavior ?? (reduced ? 'auto' : 'smooth') });
  }

  /** @param {DragEvent} event */
  #preventDrag = (event) => {
    event.preventDefault();
  };

  get #scroller() {
    return this.refs.scroller instanceof HTMLElement ? this.refs.scroller : this;
  }

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    // Primary button only; let touch use native scrolling.
    if (event.button !== 0 || event.pointerType === 'touch') return;

    const el = this.#scroller;
    if (el.scrollWidth <= el.clientWidth) return; // nothing to scroll

    this.#pointerId = event.pointerId;
    this.#startX = event.clientX;
    this.#startLeft = el.scrollLeft;
    this.#moved = false;

    el.style.cursor = 'grabbing';
    el.style.userSelect = 'none';
    // Free-drag: temporarily disable scroll snapping so it doesn't fight the drag.
    this.#savedSnap = el.style.scrollSnapType;
    el.style.scrollSnapType = 'none';

    document.addEventListener('pointermove', this.#handlePointerMove);
    document.addEventListener('pointerup', this.#handlePointerUp);
    document.addEventListener('pointercancel', this.#handlePointerUp);
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    if (event.pointerId !== this.#pointerId) return;
    const dx = event.clientX - this.#startX;
    if (Math.abs(dx) > 3) this.#moved = true;
    this.#scroller.scrollLeft = this.#startLeft - dx;
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    if (event.pointerId !== this.#pointerId) return;

    const el = this.#scroller;
    el.style.cursor = '';
    el.style.userSelect = '';
    el.style.scrollSnapType = this.#savedSnap; // snaps to nearest on release
    this.#pointerId = null;

    document.removeEventListener('pointermove', this.#handlePointerMove);
    document.removeEventListener('pointerup', this.#handlePointerUp);
    document.removeEventListener('pointercancel', this.#handlePointerUp);
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!this.#moved) return;
    event.preventDefault();
    event.stopPropagation();
    this.#moved = false;
  };
}

if (!customElements.get('drag-scroll')) {
  customElements.define('drag-scroll', DragScroll);
}
